<svelte:options runes={false} />
<script>
  // The AutoTrader deploy/parameter-entry form (U8). Reuses the Backtester's StrategyPicker / StrategyParameters
  // / SymbolMultiSelect VERBATIM (R6 — "exactly the same way"), binding the FULL Backtester-parity surface that
  // U0 landed on DeployStrategyRequest (RiskFactor, SizingLogic, BenchmarkSymbol, Tuning, EnableStopOrders) plus
  // the AutoTrader-only blast-radius caps. There is NO window (the live loop streams open-endedly).
  import { createEventDispatcher, onMount } from 'svelte';
  import StrategyPicker from '../Backtesting/StrategyPicker.svelte';
  import StrategyParameters from '../Backtesting/StrategyParameters.svelte';
  import SymbolMultiSelect, { toRequestSymbol, fromRequestSymbol } from '../Backtesting/SymbolMultiSelect.svelte';
  import OrgAllocationTable from './OrgAllocationTable.svelte';
  import TwapPolicyFields from '../shared/twap/TwapPolicyFields.svelte';
  import { DEFAULT_POLICY, validatePolicy, policyToRequest, requestToPolicy, restrictSymbols } from '../../lib/twapPolicy.js';
  import { availableOrgs, selectedOrgId } from '../../lib/orgContext.js';
  import { sandboxCredsPresent } from '../../lib/venueContext.js';
  import {
    DEFAULT_TARGET, DEPLOYABLE_TARGETS, isRealMoney, targetLabel, sameTarget, deployTargetFields,
  } from '../../lib/runContext.js';
  import { defaultStrategyId, buildTuning, strategyById, strategyByKey } from '../../lib/strategies.js';
  import {
    policyLoadState, loadStrategyPolicies, selectsOwnUniverse, forcedParamsFor, requiredFrequencyFor,
  } from '../../lib/strategyPolicy.js';
  import {
    symbolFieldState, submitGate, requestSymbols, lockedTopLevelValues, isTopLevelLocked,
    requestTopLevelValue, frequencyFieldState, SELF_UNIVERSE_DEPLOY_BLOCKED, SELF_UNIVERSE_QUOTE_BLOCKED, valueAfterLockRelease } from '../../lib/formLocking.js';
  import {
    SUPPORTED_QUOTE_CURRENCIES, DEFAULT_QUOTE_CURRENCY, pruneToQuote, formatQuoteAmount, defaultStartingCash,
  } from '../../lib/quoteCurrency.js';
  import api from '../../lib/api.js';

  const dispatch = createEventDispatcher();

  // `deploying` disables the form while the deploy POST is in flight; `serverError` is the backend's
  // plain-string 400/422 body, rendered inline at the top of the form (not a disappearing toast).
  export let deploying = false;
  export let serverError = null;
  // The RUN TARGET {venue, mode} this deploy lands on (ADR-0040): Paper on Coinbase (simulated fills,
  // credential-free), Live on Coinbase (real money), or Live on the Coinbase Sandbox (fake funds, needs the
  // CBX credential). `target` is the DEFAULT the opening view hands in; the operator may pick another of the
  // three deployable targets below unless `lockTarget` pins it (a promotion is always Live on Coinbase).
  // A real-money target fetches the available balance and bounds the allocation by the unreserved
  // remainder; the simulated targets use the default paper cash.
  export let target = DEFAULT_TARGET;
  export let lockTarget = false;
  // Promotion prefill (D4): a snapshot of the simulated strategy being promoted — seeds the form's
  // strategy/name/symbols/frequency/caps so the real-money instance starts from the proven configuration.
  export let initial = null;
  let selectedTarget = target;
  $: if (lockTarget && !sameTarget(selectedTarget, target)) selectedTarget = target;
  $: isLive = isRealMoney(selectedTarget);
  // The sandbox option is offered only with the org's CBX credential (the server refuses it otherwise).
  $: targetOptions = DEPLOYABLE_TARGETS.map((d) => ({
    ...d, disabled: deploying || lockTarget || (d.requiresSandboxCreds && !$sandboxCredsPresent),
  }));
  function selectTarget(next) {
    if (sameTarget(next, selectedTarget)) return;
    selectedTarget = next;
    loadBalance(); // the balance read is for the real-money target only (no-op otherwise)
  }

  const MAX_SYMBOLS = 10; // matches AutoTraderRequestValidator's default
  const FREQUENCIES = [
    { value: 60, label: '1m' }, { value: 300, label: '5m' }, { value: 900, label: '15m' },
    { value: 1800, label: '30m' }, { value: 3600, label: '1h' }, { value: 7200, label: '2h' },
    { value: 21600, label: '6h' }, { value: 86400, label: '1d' },
  ];
  // SizingAlgo enum ints. DEFAULT is EqualSplit (0) to match the Backtester (U0) — a deploy with no override
  // sizes identically to its backtest.
  const SIZING = [
    { value: 0, label: 'Equal Split' }, { value: 1, label: 'Inverse Volatility' }, { value: 2, label: 'ATR-based' },
  ];

  function makeDefaults() {
    return {
      strategyId: defaultStrategyId,
      quoteCurrency: DEFAULT_QUOTE_CURRENCY,
      selectedSymbols: ['BTC/USD'],
      frequencySeconds: 3600, // 1h — a sensible live cadence default
      riskFactor: 1,
      sizingLogic: 0, // EqualSplit — matches the Backtester default (U0)
      enableStopOrders: true,
      benchmarkSymbol: 'BTC/USD',
      maxNotionalPerOrder: '', // blank → server default cap
      maxOrdersPerWindow: '',  // blank → server default cap
    };
  }
  const d0 = makeDefaults();

  let strategyId = d0.strategyId;
  let selectedSymbols = d0.selectedSymbols;
  let frequencySeconds = d0.frequencySeconds;
  let riskFactor = d0.riskFactor;

  // ADR-0014: the strategy's SINGLE quote currency — picked here, permanent after deploy. The symbol
  // picker offers only same-quote pairs; switching prunes non-matching selections; the funds bar and
  // the fundability gate read the balance OF this currency (USD and USDC strictly separate).
  let quoteCurrency = d0.quoteCurrency;
  function setQuoteCurrency(c) {
    if (c === quoteCurrency) return;
    quoteCurrency = c;
    selectedSymbols = pruneToQuote(selectedSymbols, c); // deselect pairs quoted in anything else
    loadBalance(); // the balance figures are per currency — re-read for the new one
  }

  // #4: the operator-facing strategy name. Mandatory. Defaulted to a logical "{Strategy}-{Target}-{freq}"
  // SUGGESTION that auto-updates as the strategy/target/frequency changes — until the user edits it (then left alone).
  let name = '';
  let nameEdited = false;
  const FREQ_LABEL = { 60: '1m', 300: '5m', 900: '15m', 1800: '30m', 3600: '1h', 7200: '2h', 21600: '6h', 86400: '1d' };
  const TARGET_NAME = { paper: 'Paper', live: 'Live', sandbox: 'Sandbox', unknown: 'Unknown' };
  $: targetName = TARGET_NAME[targetLabel(selectedTarget)];
  $: suggestedName = `${strategyById(strategyId)?.name ?? strategyById(strategyId)?.key ?? 'Strategy'}-${targetName}-${FREQ_LABEL[frequencySeconds] ?? frequencySeconds + 's'}`;
  $: if (!nameEdited) name = suggestedName;
  function onNameInput(e) { nameEdited = true; name = e.target.value; }
  $: nameError = name.trim().length === 0 ? 'A strategy name is required.' : null;

  // #3 (Live): the caller's balance + RESERVATION overview (KTD6b), fetched on mount/target. Funds allocated
  // to other ACTIVE live strategies are reserved — only the unreserved remainder (freeUsd) may be allocated
  // here, so the per-org sliders/inputs and the inline ceiling are bounded by freeUsd, not the raw balance
  // (withdraw from another strategy to free funds). Null = unknown (read failed) → free entry, server re-validates.
  let availableUsd = null;
  let reservedUsd = 0;
  let freeUsd = null;
  let reservations = [];
  let balanceLoading = false;
  let reservationsOpen = false;
  $: fakeVenueDefaultCash = defaultStartingCash(quoteCurrency);
  $: perOrgDefaultCash = isLive
    ? (freeUsd != null ? Math.floor(freeUsd) : fakeVenueDefaultCash)
    : fakeVenueDefaultCash;

  async function loadBalance() {
    if (!isLive) return;
    // TASK-20260818: capture-then-compare — this balance sizes a REAL order, so a slow response
    // for a superseded org must never render another tenant's available balance in the form.
    const orgAtTrigger = $selectedOrgId;
    if (!orgAtTrigger) return;
    balanceLoading = true;
    try {
      // ADR-0014: the figures are for the SELECTED quote currency (the legacy *Usd field names are
      // wire-compat — the response's `currency` labels them).
      const res = await api.getAvailableBalance(selectedTarget.venue, orgAtTrigger, quoteCurrency);
      if ($selectedOrgId !== orgAtTrigger) return;
      availableUsd = res?.availableUsd ?? null;
      reservedUsd = res?.reservedUsd ?? 0;
      freeUsd = res?.freeUsd ?? (availableUsd != null ? Math.max(0, availableUsd - reservedUsd) : null);
      reservations = Array.isArray(res?.reservations) ? res.reservations : [];
    } catch {
      if ($selectedOrgId !== orgAtTrigger) return; // a stale failure must not blank newer data
      availableUsd = null;
      reservedUsd = 0;
      freeUsd = null;
      reservations = [];
    } finally {
      balanceLoading = false;
    }
  }
  onMount(() => {
    applyInitial();
    loadBalance();
    loadStrategyPolicies();
  });

  // Promotion prefill (D4): seed the form from the simulated strategy's snapshot. The strategy key on
  // the snapshot is the SAME key string the deploy sends; symbols arrive comma-joined in request form.
  function applyInitial() {
    if (!initial) return;
    const byKey = initial.strategyKey ? (strategyByKey(initial.strategyKey)?.id ?? null) : null;
    if (byKey != null) strategyId = byKey;
    if (initial.quoteCurrency && SUPPORTED_QUOTE_CURRENCIES.includes(String(initial.quoteCurrency).toUpperCase())) {
      quoteCurrency = String(initial.quoteCurrency).toUpperCase();
    }
    if (initial.symbols) {
      selectedSymbols = String(initial.symbols).split(',').map((x) => fromRequestSymbol(x.trim())).filter(Boolean);
    }
    if (initial.frequencySeconds) frequencySeconds = initial.frequencySeconds;
    if (typeof initial.enableStopOrders === 'boolean') enableStopOrders = initial.enableStopOrders;
    if (initial.maxNotionalPerOrder != null) maxNotionalPerOrder = String(initial.maxNotionalPerOrder);
    if (initial.maxOrdersPerWindow != null) maxOrdersPerWindow = String(initial.maxOrdersPerWindow);
    // The snapshot carries the EFFECTIVE policy (canonical BTC_USD symbols) — seed the form in its own display form.
    if (initial.twap) {
      const seeded = requestToPolicy(initial.twap);
      twapPolicy = { ...seeded, symbols: seeded.symbols.map((s) => fromRequestSymbol(s)) };
    }
    if (initial.name) {
      name = initial.name;
      nameEdited = true;
    }
  }

  // Amounts render in the selected quote currency (ADR-0014) — '$' for USD, '₿'/'Ξ', 'USDC' suffixed.
  $: fmtAmount = (n) => formatQuoteAmount(n, quoteCurrency);

  // Per-org allocations (KTD1/U13): the OrgAllocationTable emits the selected orgs + their cash slices and
  // whether the selection is valid (>=1 org, each cash > 0). Replaces the single startingCash.
  let allocations = [];
  let allocationsValid = false;
  function onAllocationsChange(e) {
    allocations = e.detail.allocations;
    allocationsValid = e.detail.valid;
  }
  let sizingLogic = d0.sizingLogic;
  let enableStopOrders = d0.enableStopOrders;
  let benchmarkSymbol = d0.benchmarkSymbol;
  let maxNotionalPerOrder = d0.maxNotionalPerOrder;
  let maxOrdersPerWindow = d0.maxOrdersPerWindow;

  // ---- TWAP execution policy (TASK-20260823 AC 11/12) — off by default; the shared TwapPolicyFields
  // (the manual ticket's section) edits it, lib/twapPolicy.js validates it (mirrors the server's
  // TwapPolicyValidator; the server is the guard), and submit attaches `Twap` ONLY when enabled.
  let twapPolicy = { ...DEFAULT_POLICY, symbols: [] };
  // The allow-list can only name symbols this form deploys: a symbol dropped from the picker leaves it.
  $: twapPolicy = restrictSymbols(twapPolicy, symbolList);
  // Validation compares in the form's display form; the server re-checks canonically on its side.
  $: twapValidation = validatePolicy(twapPolicy, { symbols: symbolList });

  let tuningValues = {};
  let tuningInvalidCount = 0;

  function onStrategySelect(e) {
    const id = e.detail;
    if (id === strategyId) return;
    strategyId = id;
    tuningValues = {};
  }

  $: symbolList = [...new Set(selectedSymbols.map((s) => s.trim()).filter((s) => s.length > 0))];

  // ---- Strategy policy (ADR-0024 §2) ----
  $: ownUniverse = ($policyLoadState, selectsOwnUniverse(strategyId));
  $: lockedParams = ($policyLoadState, forcedParamsFor(strategyId));
  // Locking + submit rules live in lib/formLocking.js (pure + unit-tested, shared with the backtest
  // form). isDeploy: true + the target's MODE is what gates a self-universe Live deployment off (ADR-0024
  // §7 → ADR-0040) — the server refuses it too, this just makes the refusal visible before the form is
  // filled in.
  // The strategy's required candle frequency, if it declares one (TASK-20260821). Forced + the selector
  // disabled, exactly like SizingLogic — one locking mechanism, not a second bespoke one. The server
  // enforces the same rule at the boundary, so this is a rendering of the rule, never the rule itself.
  $: frequencyField = frequencyFieldState({
    requiredFrequency: ($policyLoadState, requiredFrequencyFor(strategyId)),
    options: FREQUENCIES.map((f) => f.value),
  });
  $: if (frequencyField.locked && frequencySeconds !== frequencyField.value) {
    frequencySeconds = frequencyField.value;
  }

  $: symbolField = symbolFieldState({ policyState: $policyLoadState, ownUniverse });
  $: gate = submitGate({
    policyState: $policyLoadState, ownUniverse, isDeploy: true, mode: selectedTarget.mode, quoteCurrency,
  });
  // Mode- and quote-dependent (TASK-20260820-shishito-run-blockers): self-universe deploys resolve
  // their panel in PAPER mode in USD only, so changing either must re-evaluate this — a flat "always
  // blocked" told the operator the capability did not exist after it had shipped.
  //
  // Keyed on the two SELF-UNIVERSE reasons rather than on !gate.canSubmit (AI review): the banner pairs
  // gate.reason with hardcoded self-universe prose, so a future unrelated refusal would otherwise render
  // its headline above an explanation that has nothing to do with it.
  $: selfUniverseDeployBlocked = $policyLoadState === 'loaded' && ownUniverse
    && (gate.reason === SELF_UNIVERSE_DEPLOY_BLOCKED || gate.reason === SELF_UNIVERSE_QUOTE_BLOCKED);

  // Forced params that ride the REQUEST (not the tuning bag) are ordinary controls here, so they are
  // forced + disabled directly rather than by StrategyParameters (AI review: they rendered editable).
  $: forcedTopLevel = lockedTopLevelValues(lockedParams);
  $: if (forcedTopLevel.sizingLogic !== undefined && sizingLogic !== forcedTopLevel.sizingLogic) {
    sizingLogic = forcedTopLevel.sizingLogic;
  }
  $: if (forcedTopLevel.enableStopOrders !== undefined && enableStopOrders !== forcedTopLevel.enableStopOrders) {
    enableStopOrders = forcedTopLevel.enableStopOrders;
  }
  $: sizingLocked = isTopLevelLocked(lockedParams, 'SizingLogic');
  $: stopsLocked = isTopLevelLocked(lockedParams, 'EnableStopOrders');

  // A lock that is RELEASED must not leave the forced value behind (TASK-20261002): the select has no option for
  // 'Strategy weights' on an unlocked strategy, so the stale value rode the request invisibly and the run never traded.
  let prevSizingLocked = false;
  let prevStopsLocked = false;
  $: {
    const next = valueAfterLockRelease({ wasLocked: prevSizingLocked, isLocked: sizingLocked, current: sizingLogic, fallback: d0.sizingLogic });
    if (next !== sizingLogic) sizingLogic = next;
    if (prevSizingLocked !== sizingLocked) prevSizingLocked = sizingLocked;
  }
  $: {
    const next = valueAfterLockRelease({ wasLocked: prevStopsLocked, isLocked: stopsLocked, current: enableStopOrders, fallback: d0.enableStopOrders });
    if (next !== enableStopOrders) enableStopOrders = next;
    if (prevStopsLocked !== stopsLocked) prevStopsLocked = stopsLocked;
  }

  $: errors = computeErrors(symbolList, maxNotionalPerOrder, maxOrdersPerWindow, symbolField.requiresSymbols);
  function computeErrors(symbols, maxNotional, maxOrders, requiresSymbols) {
    const e = {};
    if (!requiresSymbols) { /* the strategy derives its own symbols — none to require */ }
    else if (symbols.length === 0) e.symbols = 'Enter at least one symbol.';
    else if (symbols.length > MAX_SYMBOLS) e.symbols = `Too many symbols — at most ${MAX_SYMBOLS}.`;
    // Per-org starting cash is validated by the OrgAllocationTable (allocationsValid); see canDeploy.
    // Blast-radius caps are optional; when present they must be positive (the server enforces the hard ceiling).
    if (maxNotional !== '' && (isNaN(Number(maxNotional)) || Number(maxNotional) <= 0)) {
      e.maxNotional = 'Max notional per order must be greater than zero (or blank for the default).';
    }
    if (maxOrders !== '' && (isNaN(Number(maxOrders)) || Number(maxOrders) <= 0 || !Number.isInteger(Number(maxOrders)))) {
      e.maxOrders = 'Max orders per window must be a positive whole number (or blank for the default).';
    }
    return e;
  }

  $: canDeploy = Object.keys(errors).length === 0 && allocationsValid && tuningInvalidCount === 0 && !nameError && !deploying && gate.canSubmit && twapValidation.ok;

  // Backend 400/422 -> field mapping (mirrors the Backtester's approach). Anything unmatched falls through to
  // the inline top-of-form banner.
  const SERVER_ERROR_MAP = [
    // First: the server names a TWAP violation `Twap.<Field> …` (TwapPolicyValidator), and `Twap.Symbols …`
    // also contains "symbol" — so this entry must win before the symbols entry below.
    { match: 'Twap.', field: 'twap' },
    { match: 'allocation', field: 'allocations' }, { match: 'participating org', field: 'allocations' },
    { match: 'symbol', field: 'symbols' }, { match: 'Symbol', field: 'symbols' },
    { match: 'frequency', field: 'frequency' }, { match: 'Frequency', field: 'frequency' },
    { match: 'MaxNotional', field: 'maxNotional' }, { match: 'MaxOrders', field: 'maxOrders' },
    { match: 'benchmark', field: 'benchmark' }, { match: 'Benchmark', field: 'benchmark' },
  ];
  $: serverFieldErrors = mapServerError(serverError);
  function mapServerError(message) {
    if (!message) return {};
    const entry = SERVER_ERROR_MAP.find((m) => message.includes(m.match));
    return entry ? { [entry.field]: message } : {};
  }
  // The unmapped remainder is shown in the top banner so no server error is ever silently dropped.
  $: bannerError = serverError && Object.keys(serverFieldErrors).length === 0 ? serverError : null;
  $: shownErrors = { ...errors, ...serverFieldErrors };

  let submitting = false; // double-submit guard

  // Scroll the at-the-action error into view when a deploy failure arrives, so a user who clicked Deploy at
  // the bottom of a scrolled form sees the failure immediately rather than a silent no-op.
  let actionErrorEl = null;
  let lastServerError = null;
  $: if (serverError && serverError !== lastServerError) {
    lastServerError = serverError;
    queueMicrotask(() => actionErrorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }
  $: if (!serverError) lastServerError = null;

  function submit() {
    if (!canDeploy || submitting) return;
    submitting = true;
    try {
      const tuning = buildTuning(tuningValues);
      const request = {
        // DeployStrategyRequest.Strategy is a STRING (the strategy key/enum name, e.g. "SimpleMovingAvg") —
        // NOT the numeric id. The Backtester sends the numeric id because BacktestRequest.Strategy is a
        // StrategyType enum (deserializes from int); the AutoTrader DTO deserializes a string, so a numeric id
        // 400s ("could not be converted to System.String"). Send the key.
        Strategy: strategyById(strategyId)?.key ?? '',
        Name: name.trim(), // #4: operator-facing name (server defaults one if somehow blank)
        // ADR-0040: a deploy targets a RUN TARGET — `venue` (2 Coinbase / 6 sandbox) + `mode` ('Paper' |
        // 'Live', REQUIRED by the server). The retired `Exchange` field is never sent (api.js refuses it).
        ...deployTargetFields(selectedTarget),
        QuoteCurrency: quoteCurrency, // ADR-0014: the strategy's single quote currency — permanent post-deploy
        Symbols: requestSymbols({ ownUniverse, selected: symbolList }),
        FrequencySeconds: frequencySeconds,
        // KTD1: per-org allocations replace the single StartingCash. Each selected org contributes its slice.
        Allocations: allocations.map((a) => ({ OrgId: a.orgId, StartingCash: a.startingCash })),
        RiskFactor: Number(riskFactor),
        // Locked fields send null — 'not specified', so the strategy decides (ADR-0024 §1).
        SizingLogic: requestTopLevelValue(lockedParams, 'SizingLogic', sizingLogic),
        EnableStopOrders: requestTopLevelValue(lockedParams, 'EnableStopOrders', enableStopOrders),
      };
      if (tuning) request.Tuning = tuning;
      const benchmark = benchmarkSymbol.trim();
      if (benchmark) request.BenchmarkSymbol = toRequestSymbol(benchmark);
      if (maxNotionalPerOrder !== '') request.MaxNotionalPerOrder = Number(maxNotionalPerOrder);
      if (maxOrdersPerWindow !== '') request.MaxOrdersPerWindow = Number(maxOrdersPerWindow);
      // contract: packages/AutoTrader.Run/Models/DeployStrategyRequest.cs `Twap` (TwapExecutionPolicy, pricing
      // NESTED — the same shape as the manual order body). Attached ONLY when enabled: absent = "not specified"
      // = market orders, exactly as before. The allow-list goes in request form (BTC-USD); the server canonicalizes.
      const twap = policyToRequest({ ...twapPolicy, symbols: twapPolicy.symbols.map((s) => toRequestSymbol(s)) });
      if (twap) request.Twap = twap;
      dispatch('deploy', request);
    } finally {
      submitting = false;
    }
  }

  export function resetToDefaults() {
    const d = makeDefaults();
    strategyId = d.strategyId;
    quoteCurrency = d.quoteCurrency;
    selectedSymbols = d.selectedSymbols;
    frequencySeconds = d.frequencySeconds;
    riskFactor = d.riskFactor;
    sizingLogic = d.sizingLogic;
    enableStopOrders = d.enableStopOrders;
    benchmarkSymbol = d.benchmarkSymbol;
    maxNotionalPerOrder = d.maxNotionalPerOrder;
    maxOrdersPerWindow = d.maxOrdersPerWindow;
    twapPolicy = { ...DEFAULT_POLICY, symbols: [] };
    tuningValues = {};
    nameEdited = false; // re-enable the auto-suggested name
    selectTarget(target); // back to the opening view's default target
  }
</script>

<form class="at-deploy-form" on:submit|preventDefault={submit}>
  {#if bannerError}
    <!-- Unmapped server error: inline at the top of the form (not a disappearing toast); submit re-enables. -->
    <div class="alert alert-danger at-form-banner" role="alert">{bannerError}</div>
  {/if}

  <div class="form-row">
    <div class="form-group form-group-full">
      <label for="at-name" class="form-label">Strategy name</label>
      <input id="at-name" type="text" class="form-input" value={name} on:input={onNameInput}
        disabled={deploying} maxlength="120" placeholder="e.g. SimpleMovingAvg-Paper-1h"
        aria-invalid={!!nameError} aria-describedby="at-name-hint" />
      <span id="at-name-hint" class="form-hint">Shown in your strategy list. Edit to rename.</span>
      {#if nameError}<span class="form-error">{nameError}</span>{/if}
    </div>
  </div>

  <!-- ADR-0040: the RUN TARGET. Exactly the three the server deploys (M7); the Live-on-Coinbase choice is
       still held behind the typed-name confirm by the host, whichever view opened this form. -->
  <div class="form-row">
    <div class="form-group form-group-full">
      <span class="form-label" id="at-target-label">Run target</span>
      <div class="at-target-toggle" role="group" aria-labelledby="at-target-label" data-testid="at-target-toggle">
        {#each targetOptions as opt (opt.label)}
          <button type="button" class="at-target-option" class:active={sameTarget(opt.target, selectedTarget)}
            class:real-money={isRealMoney(opt.target)}
            aria-pressed={sameTarget(opt.target, selectedTarget)} disabled={opt.disabled}
            title={opt.requiresSandboxCreds && !$sandboxCredsPresent ? 'Set up Coinbase Sandbox keys in the Vault to enable this target' : opt.hint}
            on:click={() => selectTarget(opt.target)}>
            {opt.label}{#if isRealMoney(opt.target)} <span class="at-target-real" aria-hidden="true">real money</span>{/if}
          </button>
        {/each}
      </div>
      <span class="form-hint">{targetOptions.find((o) => sameTarget(o.target, selectedTarget))?.hint ?? ''}</span>
    </div>
  </div>

  <StrategyPicker selectedId={strategyId} on:select={onStrategySelect} />

  {#if selfUniverseDeployBlocked}
    <!-- A disabled submit with no explanation reads as a broken form. Say why, and where to go instead.
         The headline comes from the GATE rather than being hardcoded: the block now fires for two
         different reasons (wrong mode, wrong quote currency), and a fixed "switch to Paper" line would
         tell an operator already on Paper to switch to it. -->
    <div class="at-blocked" role="status" data-testid="self-universe-deploy-blocked">
      <strong>{gate.reason}</strong>
      This strategy selects its own universe (the top 100 pairs by 20-day dollar volume), built from the
      venue's product list at deploy time. You can also backtest it; the backtester builds the same
      universe server-side.
    </div>
  {/if}

  <StrategyParameters strategyId={strategyId} values={tuningValues} {lockedParams}
    on:change={(e) => (tuningValues = e.detail)}
    on:validity={(e) => (tuningInvalidCount = e.detail)} />

  <!-- ADR-0014: the strategy's single quote currency — permanent after deploy. Drives the symbol filter,
       the funds bar, and the server-side per-currency fundability gate. -->
  <div class="form-row">
    <div class="form-group">
      <span class="form-label" id="at-quote-label">Quote currency</span>
      <div class="at-quote-toggle" role="group" aria-labelledby="at-quote-label">
        {#each SUPPORTED_QUOTE_CURRENCIES as c}
          <button type="button" class="at-quote-option" class:active={quoteCurrency === c}
            aria-pressed={quoteCurrency === c} disabled={deploying} on:click={() => setQuoteCurrency(c)}>
            {c}
          </button>
        {/each}
      </div>
      <span class="form-hint">
        All pairs, books, and funding for this strategy are denominated in {quoteCurrency}. Permanent after deploy.
      </span>
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="at-symbols" class="form-label">Symbols</label>
      <SymbolMultiSelect id="at-symbols" bind:selected={selectedSymbols} max={MAX_SYMBOLS}
        quoteCurrency={quoteCurrency}
        locked={symbolField.locked}
        lockedNote="This strategy selects its own universe — the top 100 pairs by 20-day dollar volume, refreshed daily."
        invalid={!!shownErrors.symbols} disabled={deploying} describedby="at-symbols-hint" />
      <span id="at-symbols-hint" class="form-hint">
        {#if ownUniverse}The strategy picks its own pairs; there is nothing to choose here.
        {:else}Search and select up to {MAX_SYMBOLS} {quoteCurrency}-quoted pairs.{/if}
      </span>
      {#if shownErrors.symbols}<span class="form-error">{shownErrors.symbols}</span>{/if}
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="at-freq" class="form-label">Candle frequency</label>
      <select id="at-freq" class="form-select" bind:value={frequencySeconds}
        disabled={frequencyField.locked}
        data-testid="at-freq-select">
        {#each FREQUENCIES as f}<option value={f.value}>{f.label}</option>{/each}
      </select>
      <span class="form-hint">
        {#if frequencyField.locked}
          Set by the strategy — its warm-up, maximum hold and rebalance rules are all counted in bars, so
          it runs on this frequency only.
        {:else}How often the live loop ticks.{/if}
      </span>
      {#if shownErrors.frequency}<span class="form-error">{shownErrors.frequency}</span>{/if}
    </div>
  </div>

  <!-- #3 (Live): balance + RESERVATION visibility (KTD6b). Funds allocated to other active live strategies are
       reserved; only the unreserved remainder is allocatable here. Per-org amount sliders live on each org row
       below (one per org — never one common slider). -->
  {#if isLive}
    <div class="form-row">
      <div class="form-group form-group-full at-balance-block">
        <div class="at-balance-head">
          <span class="form-label">{quoteCurrency} balance</span>
          <span class="at-balance-value">
            {#if balanceLoading}<span class="spinner"></span> Reading…
            {:else if availableUsd != null}{fmtAmount(availableUsd)}
            {:else}<span class="at-balance-unknown">Unavailable</span>{/if}
          </span>
        </div>
        {#if !balanceLoading && availableUsd != null}
          <div class="at-balance-breakdown">
            <span class="at-balance-chip at-balance-reserved" title="Allocated to your other active live strategies — withdraw from one to free funds">
              Reserved {fmtAmount(reservedUsd)}
            </span>
            <span class="at-balance-chip at-balance-free" title="Unreserved — available to allocate to this strategy">
              Available to allocate {fmtAmount(freeUsd ?? 0)}
            </span>
          </div>
          {#if reservations.length > 0}
            <button type="button" class="at-reservations-toggle" on:click={() => (reservationsOpen = !reservationsOpen)}
              aria-expanded={reservationsOpen}>
              {reservationsOpen ? '▾' : '▸'} Where are my reserved funds? ({reservations.length} allocation{reservations.length === 1 ? '' : 's'})
            </button>
            {#if reservationsOpen}
              <ul class="at-reservations-list">
                {#each reservations as r}
                  <li>
                    <span class="at-res-strategy">{r.strategyName}</span>
                    <span class="at-res-org">{r.orgId}</span>
                    <span class="at-res-status">{r.status}</span>
                    <span class="at-res-amount">{fmtAmount(r.reservedUsd)}</span>
                  </li>
                {/each}
              </ul>
            {/if}
          {/if}
          {#if (freeUsd ?? 0) <= 0}
            <!-- ADR-0014 (owner decision: prompt only, no in-app conversion): a live deploy needs balance
                 IN the selected quote currency — rich balances in other currencies do not fund it. -->
            <span class="form-error">
              {#if (availableUsd ?? 0) <= 0}
                No {quoteCurrency} balance on your Coinbase account. Deposit or convert funds to
                {quoteCurrency} on Coinbase first, then reopen this form.
              {:else}
                All of your {quoteCurrency} balance is reserved by other live strategies — withdraw funds
                from one of them (or convert more to {quoteCurrency} on Coinbase) to deploy this strategy.
              {/if}
            </span>
          {/if}
        {:else if !balanceLoading}
          <span class="form-hint">Balance couldn't be read — enter starting cash manually; the server validates it at deploy.</span>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Per-org capital allocation (KTD1/U13, #2 redesign): collapsible; at least the user's own org selected by
       default. Live ceiling = the UNRESERVED free USD (KTD6b), with a per-org amount slider on each row. -->
  <div class="form-row">
    <div class="form-group form-group-full">
      <OrgAllocationTable orgs={$availableOrgs} disabled={deploying}
        defaultOrgId={$selectedOrgId}
        defaultCash={perOrgDefaultCash}
        currency={quoteCurrency}
        maxTotal={isLive ? freeUsd : null}
        on:change={onAllocationsChange} />
      {#if shownErrors.allocations}<span class="form-error">{shownErrors.allocations}</span>{/if}
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="at-sizing" class="form-label">Position sizing</label>
      <select id="at-sizing" class="form-select" bind:value={sizingLogic} disabled={sizingLocked}>
        {#each SIZING as s}<option value={s.value}>{s.label}</option>{/each}
        {#if sizingLocked}<option value={3}>Strategy weights</option>{/if}
      </select>
      <span class="form-hint">
        {#if sizingLocked}Set by the strategy — it computes its own position weights.
        {:else}How capital is allocated across positions.{/if}
      </span>
    </div>
    <div class="form-group">
      <label for="at-risk" class="form-label">Risk factor</label>
      <input id="at-risk" type="number" min="0" step="0.1" class="form-input" bind:value={riskFactor} />
      <span class="form-hint">Scales position size; 1 is the neutral default.</span>
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="at-benchmark" class="form-label">Benchmark (optional)</label>
      <input id="at-benchmark" type="text" class="form-input" placeholder="e.g. BTC/USD"
        bind:value={benchmarkSymbol} disabled={deploying} aria-describedby="at-benchmark-hint" />
      <span id="at-benchmark-hint" class="form-hint">Compare against this symbol. Leave blank for none.</span>
      {#if shownErrors.benchmark}<span class="form-error">{shownErrors.benchmark}</span>{/if}
    </div>
  </div>

  <fieldset class="at-blast-radius">
    <legend>Blast-radius caps (optional)</legend>
    <div class="form-row">
      <div class="form-group">
        <label for="at-max-notional" class="form-label">Max notional / order</label>
        <input id="at-max-notional" type="number" min="0" step="any" class="form-input"
          placeholder="server default" bind:value={maxNotionalPerOrder} disabled={deploying}
          aria-invalid={!!shownErrors.maxNotional} />
        {#if shownErrors.maxNotional}<span class="form-error">{shownErrors.maxNotional}</span>{/if}
      </div>
      <div class="form-group">
        <label for="at-max-orders" class="form-label">Max orders / window</label>
        <input id="at-max-orders" type="number" min="0" step="1" class="form-input"
          placeholder="server default" bind:value={maxOrdersPerWindow} disabled={deploying}
          aria-invalid={!!shownErrors.maxOrders} />
        {#if shownErrors.maxOrders}<span class="form-error">{shownErrors.maxOrders}</span>{/if}
      </div>
    </div>
    <span class="form-hint">
      Tighten the per-order notional / per-window order count. Blank uses the server's safety ceiling; a value
      above it is rejected.
    </span>
  </fieldset>

  <!-- TWAP execution (TASK-20260823 AC 11/12): the deployment's execution policy — off by default. The
       strategy never knows: signals, sizing and protective stops are untouched; entries/exits on the chosen
       symbols are placed as TWAP parents the host slices. Same section as the manual ticket (shared fields). -->
  <div class="at-twap" aria-describedby="at-twap-hint">
    <TwapPolicyFields bind:policy={twapPolicy} symbols={symbolList} errors={twapValidation.errors}
      warnings={twapValidation.warnings} showConcurrency={true} disabled={deploying} idPrefix="at-twap" />
    <span id="at-twap-hint" class="form-hint">
      TWAP execution: entries and exits are worked as time-sliced parent orders over the duration instead of one
      market order. Protective stops stay native. Editable later on the Parameters tab; the change applies to the
      next order — in-flight parents are not changed.
    </span>
    {#if shownErrors.twap}<span class="form-error">{shownErrors.twap}</span>{/if}
  </div>

  <div class="form-row">
    <label class="bt-checkbox" class:locked-row={stopsLocked}>
      <input type="checkbox" bind:checked={enableStopOrders} disabled={stopsLocked} />
      <span>
        Enable protective stop orders
        {#if stopsLocked}<em> — set by the strategy: its own exit rules are the exit engine</em>{/if}
      </span>
    </label>
  </div>

  <!-- Error shown RIGHT AT the action (where the user clicked), so a failed deploy is never missed — the
       top-of-form banner alone is easy to miss when the button is far down a scrolled form. -->
  {#if serverError}
    <div class="alert alert-danger at-deploy-action-error" role="alert" bind:this={actionErrorEl}>
      Deploy failed: {serverError}
    </div>
  {/if}

  <div class="form-actions">
    <button type="submit" class="btn btn-primary btn-lg" disabled={!canDeploy}>
      {#if deploying}<span class="spinner"></span> Deploying…{:else}Deploy strategy{/if}
    </button>
    <button type="button" class="btn btn-ghost btn-lg" disabled={deploying}
      on:click={() => dispatch('reset')}>
      Reset to defaults
    </button>
  </div>
</form>

<style>
  @import '../Backtesting/BacktestForm.css';

  /* ADR-0040: the run-target picker — the same button-group pattern as the quote picker below. */
  .at-target-toggle { display: inline-flex; flex-wrap: wrap; gap: 0; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; overflow: hidden; width: fit-content; }
  .at-target-option {
    appearance: none; background: transparent; border: none; cursor: pointer;
    padding: 0.4rem 0.9rem; font-size: 0.82rem; font-weight: 600; color: var(--text-secondary, #b5b6b8);
  }
  .at-target-option + .at-target-option { border-left: 1px solid var(--border-subtle, #1e1f22); }
  .at-target-option.active { background: var(--at-accent-paper, #5b8def); color: #fff; }
  .at-target-option.real-money.active { background: var(--at-accent-live, #ff7a45); color: #1a0c06; }
  .at-target-option:disabled { cursor: not-allowed; opacity: 0.6; }
  .at-target-real { font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase; opacity: 0.85; margin-left: 0.3rem; }

  /* ADR-0014: the quote-currency picker — mirrors App.svelte's .venue-toggle button-group pattern. */
  .at-quote-toggle { display: inline-flex; gap: 0; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; overflow: hidden; width: fit-content; }
  .at-quote-option {
    appearance: none; background: transparent; border: none; cursor: pointer;
    padding: 0.35rem 0.9rem; font-size: 0.82rem; font-weight: 600; color: var(--text-secondary, #b5b6b8);
  }
  .at-quote-option + .at-quote-option { border-left: 1px solid var(--border-subtle, #1e1f22); }
  .at-quote-option.active { background: var(--accent, #2563eb); color: #fff; }
  .at-quote-option:disabled { cursor: not-allowed; opacity: 0.6; }

  .at-balance-block { display: flex; flex-direction: column; gap: 0.4rem; }
  .at-balance-head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; }
  .at-balance-value { font-size: 1rem; font-weight: 700; color: var(--text-primary, #e8e9ea); font-variant-numeric: tabular-nums; }
  .at-balance-unknown { color: var(--text-muted, #8a8b8d); font-weight: 400; }
  .at-balance-breakdown { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .at-balance-chip {
    font-size: 0.78rem; font-weight: 600; padding: 0.2rem 0.6rem; border-radius: 999px;
    font-variant-numeric: tabular-nums; border: 1px solid var(--border-subtle, #1e1f22);
  }
  .at-balance-reserved { color: #e8a33c; border-color: rgba(232, 163, 60, 0.4); background: rgba(232, 163, 60, 0.08); }
  .at-balance-free { color: var(--success, #1aa260); border-color: rgba(26, 162, 96, 0.4); background: rgba(26, 162, 96, 0.08); }
  .at-reservations-toggle {
    appearance: none; background: transparent; border: none; cursor: pointer; text-align: left;
    font-size: 0.78rem; color: var(--text-secondary, #b5b6b8); padding: 0.1rem 0;
  }
  .at-reservations-toggle:hover { color: var(--text-primary, #e8e9ea); }
  .at-reservations-list { list-style: none; margin: 0.2rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.2rem; }
  .at-reservations-list li {
    display: flex; gap: 0.6rem; align-items: baseline; font-size: 0.78rem;
    padding: 0.25rem 0.5rem; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 4px;
  }
  .at-res-strategy { font-weight: 600; color: var(--text-primary, #e8e9ea); }
  .at-res-org { color: var(--text-muted, #8a8b8d); }
  .at-res-status { color: var(--text-muted, #8a8b8d); font-size: 0.72rem; text-transform: uppercase; }
  .at-res-amount { margin-left: auto; font-variant-numeric: tabular-nums; color: var(--text-secondary, #b5b6b8); }

  /* ADR-0024 §7: self-universe deployment is gated off until the live-scale work lands. */
  .at-blocked {
    margin: 0 0 var(--spacing-base, 1rem);
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-primary, #2a2b2e);
    border-left: 3px solid var(--color-warning, #d99a2b);
    border-radius: 6px;
    background: var(--bg-secondary, #141517);
    color: var(--text-secondary, #b5b6b8);
    font-size: 0.85rem;
    line-height: 1.5;
  }
  .at-blocked strong { display: block; color: var(--text-primary, #e8e9ea); margin-bottom: 0.15rem; }
</style>
